import { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { inputClass } from './Field';

export interface TypeAheadItem {
  key: string;
  label: string;
  sublabel?: string;
}

interface TypeAheadProps<T extends TypeAheadItem> {
  items: T[];
  onSelect: (item: T) => void;
  placeholder?: string;
  id?: string;
  autoFocus?: boolean;
  /** Called when Escape is pressed while the dropdown is already closed — lets a parent (e.g. a modal) handle it. */
  onEscapeWhenClosed?: () => void;
  emptyMessage?: string;
  disabled?: boolean;
  className?: string;
  /** Optional hook for server-backed typeahead wrappers. */
  onQueryChange?: (query: string) => void;
}

function fuzzyScore(query: string, label: string): number {
  const q = query.toLowerCase();
  const l = label.toLowerCase();
  if (q === '') return 1;
  const idx = l.indexOf(q);
  if (idx === 0) return 100; // prefix match wins
  if (idx > 0) return 60 - idx; // substring match, earlier is better
  // subsequence fallback so "azd" still finds "Azad"
  let qi = 0;
  for (let li = 0; li < l.length && qi < q.length; li++) {
    if (l[li] === q[qi]) qi++;
  }
  return qi === q.length ? 10 : -1;
}

/**
 * Fuzzy type-ahead selector (PRD §12.2): filters on every keystroke, arrows
 * move the highlight, Enter accepts — no click ever required. Local data
 * only, so filtering is synchronous and effectively instant.
 */
export function TypeAhead<T extends TypeAheadItem>({
  items,
  onSelect,
  placeholder,
  id,
  autoFocus,
  onEscapeWhenClosed,
  emptyMessage = 'No matches',
  disabled,
  className,
  onQueryChange,
}: TypeAheadProps<T>) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);

  const filtered = useMemo(() => {
    if (query === '') return items.slice(0, 50);
    return items
      .map((item) => ({ item, score: fuzzyScore(query, item.label) }))
      .filter((r) => r.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 50)
      .map((r) => r.item);
  }, [items, query]);

  useEffect(() => {
    setHighlight(0);
  }, [query, open]);

  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.children[highlight] as HTMLElement | undefined;
    el?.scrollIntoView({ block: 'nearest' });
  }, [highlight, open]);

  function commit(item: T) {
    onSelect(item);
    setQuery('');
    onQueryChange?.('');
    setOpen(false);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      setHighlight((h) => (filtered.length === 0 ? 0 : (h + 1) % filtered.length));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (open) setHighlight((h) => (filtered.length === 0 ? 0 : (h - 1 + filtered.length) % filtered.length));
    } else if (e.key === 'Enter') {
      if (open && filtered.length > 0) {
        e.preventDefault();
        const chosen = filtered[highlight] ?? filtered[0];
        if (chosen) commit(chosen);
      }
    } else if (e.key === 'Escape') {
      if (open) {
        e.preventDefault();
        e.stopPropagation();
        setOpen(false);
      } else {
        onEscapeWhenClosed?.();
      }
    }
  }

  return (
    <div className={clsx('relative', className)}>
      <input
        id={id}
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        autoComplete="off"
        disabled={disabled}
        autoFocus={autoFocus}
        className={inputClass}
        placeholder={placeholder}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          onQueryChange?.(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
        onKeyDown={handleKeyDown}
      />
      {open && (
        <ul
          ref={listRef}
          role="listbox"
          className="absolute z-40 mt-0.5 max-h-64 w-full min-w-[16rem] overflow-y-auto border border-[var(--border-strong)] bg-[var(--bg-elevated)]"
        >
          {filtered.length === 0 && <li className="px-2 py-1.5 text-[12px] text-[var(--text-faint)]">{emptyMessage}</li>}
          {filtered.map((item, i) => (
            <li
              key={item.key}
              role="option"
              aria-selected={i === highlight}
              onMouseDown={(e) => {
                e.preventDefault();
                commit(item);
              }}
              onMouseEnter={() => setHighlight(i)}
              className={clsx(
                'flex cursor-pointer items-baseline justify-between gap-2 px-2 py-1 text-[12.5px]',
                i === highlight ? 'bg-[var(--bg-selected)]' : '',
              )}
            >
              <span>{item.label}</span>
              {item.sublabel && <span className="text-[11px] text-[var(--text-faint)]">{item.sublabel}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
